import { useState, useEffect } from 'react'
import DashboardLayout from '../app-layout'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import WrapperCard from '@/components/fragments/wrapper-card'
import { CreditCardIcon, PercentIcon } from '@/assets/svg'
import {
  RevenueBreakdownPreview,
  Notifications,
  MenuCategories,
  SystemSettings,
  BusinessTypes,
} from './components'
import { FinancialSettingsSkeleton } from '@/skeletons'
import {
  useGetSettingsQuery,
  useUpdateSettingsMutation,
} from '@/redux/services/settings'
import { toast } from 'sonner'

// Extract financial settings from API data
const getFinancialSettingsFromAPI = (data: any) => {
  if (!data?.settings?.financial) {
    return {}
  }
  const financial = data.settings.financial
  const settingsMap: Record<string, string> = {}

  // Extract commission_service_fees
  if (financial.commission_service_fees) {
    financial.commission_service_fees.forEach((item: any) => {
      settingsMap[item.key] = item.value?.toString() || '0'
    })
  }

  // Extract payment_processing
  if (financial.payment_processing) {
    financial.payment_processing.forEach((item: any) => {
      settingsMap[item.key] = item.value?.toString() || '0'
    })
  }

  // Extract revenue_breakdown
  if (financial.revenue_breakdown) {
    financial.revenue_breakdown.forEach((item: any) => {
      settingsMap[item.key] = item.value?.toString() || '0'
    })
  }

  // Extract withdrawal_settings
  if (financial.withdrawal_settings) {
    financial.withdrawal_settings.forEach((item: any) => {
      settingsMap[item.key] = item.value?.toString() || '0'
    })
  }

  return settingsMap
}

export default function Settings() {
  const { data, isLoading } = useGetSettingsQuery({})
  const [updateSettings, { isLoading: isSavingSettings }] =
    useUpdateSettingsMutation()

  const [activeTab, setActiveTab] = useState<string>('financial')
  const [financialSettings, setFinancialSettings] = useState(
    getFinancialSettingsFromAPI(data)
  )
  const [originalSettings, setOriginalSettings] = useState(
    getFinancialSettingsFromAPI(data)
  )

  // Update financial settings when API data changes
  useEffect(() => {
    if (data?.settings?.financial) {
      const newSettings = getFinancialSettingsFromAPI(data)
      setFinancialSettings(newSettings)
      setOriginalSettings(newSettings)
    }
  }, [data])

  const handleFinancialChange = (field: string, value: string) => {
    setFinancialSettings((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  // Get only the changed fields as array
  const getChangedFields = () => {
    return Object.keys(financialSettings)
      .filter((key) => financialSettings[key] !== originalSettings[key])
      .map((key) => ({
        key,
        value: parseFloat(financialSettings[key]) || financialSettings[key],
      }))
  }

  const handleSaveSettings = async () => {
    const changedFields = getChangedFields()

    if (changedFields.length === 0) {
      toast.info('No changes to save')
      return
    }

    updateSettings({ settings: changedFields })
      .unwrap()
      .then((response: any) => {
        if (response.success) {
          toast.success(response.message)
        } else {
          toast.error(response.message)
        }
      })
      .catch((error: any) => {
        toast.error(error.data.message)
      })
  }

  const handleResetSettings = () => {
    const resetSettings = getFinancialSettingsFromAPI(data)
    setFinancialSettings(resetSettings)
    setOriginalSettings(resetSettings)
  }

  return (
    <DashboardLayout pageTitle='Platform Settings' innerClassName='pt-1'>
      <section className='w-full h-full overflow-hidden'>
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className='w-full h-full flex flex-col'
        >
          <div className='flex flex-row items-center justify-between overflow-x-auto custom-scroll'>
            <TabsList className='flex-shrink-0 bg-white'>
              <TabsTrigger value='financial' className='hover:cursor-pointer'>
                Financial Settings
              </TabsTrigger>
              <TabsTrigger
                value='notifications'
                className='hover:cursor-pointer'
              >
                Notifications
              </TabsTrigger>
              <TabsTrigger
                value='business-types'
                className='hover:cursor-pointer'
              >
                Business Types
              </TabsTrigger>
              <TabsTrigger
                value='sub-categories'
                className='hover:cursor-pointer'
              >
                Sub-Categories
              </TabsTrigger>
              <TabsTrigger value='system' className='hover:cursor-pointer'>
                System Settings
              </TabsTrigger>
              {/* <TabsTrigger value='security' className='hover:cursor-pointer'>
                Security
              </TabsTrigger> */}
            </TabsList>
          </div>

          <TabsContent value='financial' className='flex-1'>
            {isLoading ? (
              <FinancialSettingsSkeleton />
            ) : (
              <div className='space-y-6 mt-4'>
                <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                  <WrapperCard
                    title='Commission & Service Fees'
                    icon={PercentIcon}
                    wrapperClassName='h-fit'
                    headerClassName='flex flex-row-reverse justify-end items-center gap-2'
                  >
                    <div className='space-y-6 mt-4'>
                      <div className='bg-[#FAFAFA] rounded-[1.15rem] p-4'>
                        <Input
                          id='service-charge'
                          name='service_charge_percentage'
                          label='Service Charge Percentage'
                          labelClass='text-[14px] SFProRegular'
                          type='number-mance'
                          value={financialSettings.service_charge_percentage}
                          onChange={(e) =>
                            handleFinancialChange(
                              'service_charge_percentage',
                              e.target.value
                            )
                          }
                          suffix='%'
                          containerClass='mb-2'
                          inputClass='!bg-white !rounded-[8px] !border-0'
                          removeArrows={false}
                        />
                        <p className='text-xs text-gray-500 mt-1'>
                          Applied to all orders
                        </p>
                      </div>

                      <div className='bg-[#FAFAFA] rounded-[1.15rem] p-4'>
                        <Input
                          id='delivery-charge'
                          name='delivery_charge_percentage'
                          label='Delivery Charge Percentage'
                          labelClass='text-[14px] SFProRegular'
                          type='number-mance'
                          value={financialSettings.delivery_charge_percentage}
                          onChange={(e) =>
                            handleFinancialChange(
                              'delivery_charge_percentage',
                              e.target.value
                            )
                          }
                          suffix='%'
                          containerClass='mb-2'
                          inputClass='!bg-white !rounded-[8px] !border-0'
                          removeArrows={false}
                        />
                        <p className='text-xs text-gray-500 mt-1'>
                          Percentage of order value for delivery
                        </p>
                      </div>

                      <div className='bg-[#FAFAFA] rounded-[1.15rem] p-4'>
                        <Input
                          id='vendor-commission'
                          name='vendor_commission'
                          label='Vendor Commission'
                          labelClass='text-[14px] SFProRegular'
                          type='number-mance'
                          value={financialSettings.vendor_commission}
                          onChange={(e) =>
                            handleFinancialChange(
                              'vendor_commission',
                              e.target.value
                            )
                          }
                          suffix='%'
                          containerClass='mb-2'
                          inputClass='!bg-white !rounded-[8px] !border-0'
                        />
                        <p className='text-xs text-gray-500 mt-1'>
                          Commission percentage for vendors
                        </p>
                      </div>
                      <div className='bg-[#FAFAFA] rounded-[1.15rem] p-4'>
                        <Input
                          id='platform-commission'
                          name='platform_commission_rate'
                          label='FoodXL Platform Commission'
                          labelClass='text-[14px] SFProRegular'
                          type='number-mance'
                          value={financialSettings.platform_commission_rate}
                          onChange={(e) =>
                            handleFinancialChange(
                              'platform_commission_rate',
                              e.target.value
                            )
                          }
                          suffix='%'
                          containerClass='mb-2'
                          inputClass='!bg-white !rounded-[8px] !border-0'
                        />
                        <p className='text-xs text-gray-500 mt-1'>
                          Total platform commission
                        </p>
                      </div>
                    </div>
                  </WrapperCard>

                  <WrapperCard
                    title='Payment Processing'
                    icon={CreditCardIcon}
                    wrapperClassName='h-fit'
                    headerClassName='flex flex-row-reverse justify-end items-center gap-2'
                  >
                    <div className='space-y-6 mt-4'>
                      <div className='bg-[#FAFAFA] rounded-[1.15rem] p-4'>
                        <Input
                          id='stripe-fee'
                          name='stripe_fee'
                          label='Stripe Fee'
                          type='number-mance'
                          value={financialSettings.stripe_fee}
                          onChange={(e) =>
                            handleFinancialChange('stripe_fee', e.target.value)
                          }
                          suffix='%'
                          containerClass='mb-2'
                          labelClass='text-[14px] SFProRegular'
                          inputClass='!bg-white !rounded-[8px] !border-0'
                        />
                        <p className='text-xs text-gray-500 mt-1'>
                          Payment processing fee
                        </p>
                      </div>

                      <div className='bg-[#FAFAFA] rounded-[1.15rem] p-4'>
                        <Input
                          id='tax-rate'
                          name='tax_rate'
                          label='Tax Rate'
                          type='number-mance'
                          value={financialSettings.tax_rate}
                          onChange={(e) =>
                            handleFinancialChange('tax_rate', e.target.value)
                          }
                          suffix='%'
                          containerClass='mb-2'
                          labelClass='text-[14px] SFProRegular'
                          inputClass='!bg-white !rounded-[8px] !border-0'
                        />
                        <p className='text-xs text-gray-500 mt-1'>
                          Default tax rate for orders
                        </p>
                      </div>

                      <div className='bg-[#FAFAFA] rounded-[1.15rem] p-4'>
                        <Input
                          id='minimum-withdrawal'
                          name='minimum_withdrawal_amount'
                          label='Minimum Withdrawal'
                          type='number-mance'
                          value={financialSettings.minimum_withdrawal_amount}
                          onChange={(e) =>
                            handleFinancialChange(
                              'minimum_withdrawal_amount',
                              e.target.value
                            )
                          }
                          prefix='$'
                          containerClass='mb-2'
                          labelClass='text-[14px] SFProRegular'
                          inputClass='!bg-white !rounded-[8px] !border-0'
                        />
                        <p className='text-xs text-gray-500 mt-1'>
                          Minimum amount for vendor withdrawals
                        </p>
                      </div>
                      <div className='bg-[#FAFAFA] rounded-[1.15rem] p-4'>
                        <Input
                          id='maximum-withdrawal'
                          name='maximum_withdrawal_amount'
                          label='Maximum Withdrawal'
                          type='number-mance'
                          value={financialSettings.maximum_withdrawal_amount}
                          onChange={(e) =>
                            handleFinancialChange(
                              'maximum_withdrawal_amount',
                              e.target.value
                            )
                          }
                          prefix='$'
                          containerClass='mb-2'
                          labelClass='text-[14px] SFProRegular'
                          inputClass='!bg-white !rounded-[8px] !border-0'
                        />
                        <p className='text-xs text-gray-500 mt-1'>
                          Maximum amount for vendor withdrawals
                        </p>
                      </div>
                    </div>
                  </WrapperCard>
                </div>

                <RevenueBreakdownPreview
                  financialSettings={financialSettings}
                  onSave={handleSaveSettings}
                  onReset={handleResetSettings}
                  isSaving={isSavingSettings}
                />
              </div>
            )}
          </TabsContent>

          <TabsContent value='notifications' className='flex-1'>
            <Notifications
              data={data?.settings?.notifications}
              isLoading={isSavingSettings}
              isLoadingSettings={isLoading}
            />
          </TabsContent>

          <TabsContent value='sub-categories' className='flex-1'>
            <MenuCategories />
          </TabsContent>

          <TabsContent value='business-types' className='flex-1'>
            <BusinessTypes />
          </TabsContent>

          <TabsContent value='system' className='flex-1'>
            <SystemSettings
              data={data?.settings?.system}
              isLoadingSettings={isLoading}
            />
          </TabsContent>

          <TabsContent value='security' className='flex-1'>
            <div className='flex items-center justify-center h-full'>
              <p className='text-gray-500'>Security settings coming soon...</p>
            </div>
          </TabsContent>
        </Tabs>
      </section>
    </DashboardLayout>
  )
}
